import React, { useState } from "react";
import PropTypes from "prop-types";
import PerfectScrollbar from "react-perfect-scrollbar";
import orderBy from "lodash/orderBy";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import LinearProgress from "@mui/material/LinearProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";

import Header from "./Header";
import NoRecordsTable from "./NoRecordsTable";
import EntityTableRow from "./EntityTableRow";

export const applyPagination = (entities, page, limit) => {
  return entities.slice(page * limit, page * limit + limit);
};

export const EntityTable = ({
  className,
  configuration,
  entities,
  setEntities,
  page,
  limit,
  loading,
  sort,
}) => {
  const [selected, setSelected] = useState(null);
  const [orderDirection, setorderDirection] = useState(sort?.direction || "asc");
  const [valueToOrderBy, setvalueToOrderBy] = useState(sort?.order);

  const handleSort = value => {
    setvalueToOrderBy(value);
    setorderDirection(v => (v === "asc" ? "desc" : "asc"));
  };

  const tableWithActions =
    configuration.editLink || configuration.customLink || configuration.removeMutation || configuration.actions;

  let strategy = "renderTable";
  if (entities.length === 0) {
    strategy = "noRecords";
  }
  if (loading) {
    strategy = "loading";
  }

  return (
    <Table className={className} stickyHeader>
      <TableHead>
        <TableRow
          sx={{
            bgcolor: "grey.50",
            "& .MuiTableCell-head": {
              borderBottom: "2px solid",
              borderColor: "divider",
              py: 2,
              fontSize: "0.875rem",
              fontWeight: 500,
              color: "text.primary",
              letterSpacing: "0.05em",
            },
          }}
        >
          {configuration.fields.map(({ label, path, headerAlign }, index) => (
            <TableCell
              key={`tablecell-${configuration.title}-${index}`}
              align={headerAlign || "left"}
            >
              {sort ? (
                <TableSortLabel
                  active={valueToOrderBy === path}
                  direction={valueToOrderBy === path ? orderDirection : "asc"}
                  onClick={() => handleSort(path)}
                  sx={{
                    "&.Mui-active": {
                      color: "primary.600",
                      "& .MuiTableSortLabel-icon": {
                        color: "primary.600 !important",
                      },
                    },
                  }}
                >
                  {label}
                </TableSortLabel>
              ) : (
                label
              )}
            </TableCell>
          ))}
          {configuration.nestedTable && <TableCell>{configuration.nestedTable.title}</TableCell>}
          {tableWithActions && <TableCell align="right">Actions</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {strategy === "loading" && (
          <TableRow>
            <TableCell colSpan={configuration.fields.length + 2}>
              <LinearProgress className="mt-2" />
            </TableCell>
          </TableRow>
        )}
        {strategy === "noRecords" && <NoRecordsTable configuration={configuration} />}
        {strategy === "renderTable" &&
          applyPagination(
            orderBy(entities, valueToOrderBy, orderDirection),
            page,
            limit
          ).map((entity, index) => (
            <EntityTableRow
              configuration={configuration}
              entity={entity}
              setEntities={setEntities}
              key={`${entity.id || index}`}
              selected={selected}
              setSelected={setSelected}
            />
          ))}
      </TableBody>
    </Table>
  );
};

const Results = ({
  setEntities,
  entities,
  configuration,
  loading,
  sort,
  rowsPerPage = 10,
  scrollable = true,
  disablePagination = false,
  scrollbarStyle,
}) => {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(rowsPerPage);

  const displayLimit = disablePagination ? entities.length || 0 : limit;

  const handlePageChange = (event, newPage) => {
    if (!disablePagination) setPage(newPage);
  };

  const handleLimitChange = event => {
    if (!disablePagination) setLimit(parseInt(event.target.value));
  };

  return (
    <Paper sx={{ mt: 2, p: 2 }}>
      <Header configuration={configuration} />
      <div>
        <Typography
          color="text.secondary"
          gutterBottom
          variant="body2"
          sx={{
            mb: 1,
            fontSize: "0.875rem",
            fontWeight: 400,
          }}
        >
          {entities.length} Records Found
          {!disablePagination && (
            <>
              . Page {page + 1} of {Math.ceil(entities.length / displayLimit)}
            </>
          )}
        </Typography>
        <Box>
          {scrollable ? (
            <PerfectScrollbar style={scrollbarStyle}>
              <Box>
                <EntityTable
                  configuration={configuration}
                  setEntities={setEntities}
                  entities={entities}
                  page={page}
                  limit={displayLimit}
                  loading={loading}
                  sort={sort}
                />
              </Box>
            </PerfectScrollbar>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <EntityTable
                configuration={configuration}
                setEntities={setEntities}
                entities={entities}
                page={page}
                limit={displayLimit}
                loading={loading}
                sort={sort}
              />
            </Box>
          )}
          {!disablePagination && (
            <TablePagination
              component="div"
              count={entities.length}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
              page={page}
              rowsPerPage={displayLimit}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: "grey.25",
                px: 3,
                py: 1.5,
                "& p": {
                  marginBottom: 0,
                  fontSize: "0.875rem",
                  color: "text.secondary",
                },
                "& .MuiTablePagination-select": {
                  borderRadius: 1,
                  bgcolor: "white",
                  border: "1px solid",
                  borderColor: "grey.300",
                },
                "& .MuiTablePagination-actions button": {
                  borderRadius: 1,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: "primary.50",
                    color: "primary.600",
                  },
                },
              }}
            />
          )}
        </Box>
      </div>
    </Paper>
  );
};

Results.propTypes = {
  className: PropTypes.string,
  entities: PropTypes.array.isRequired,
  scrollable: PropTypes.bool,
  disablePagination: PropTypes.bool,
};

Results.defaultProps = {
  entities: [],
  disablePagination: false,
};

export default Results;
